import type {
  ExamFormTestChoice,
  Height,
  Soap,
  SubjectiveExamForm,
  ExamFormTestData,
  ReferralHistory,
  HealthHistory,
  InjuryOnsetWindow,
} from "@rmh/defs";
import { TestKey, TestRowKey } from "@rmh/defs";
import dayjs from "dayjs";
import { title } from "../strings";

export function getSuicidalPHQ9ScoreFromSubjectiveExam(
  subjectiveExam: SubjectiveExamForm
) {
  const suicidalQuestion = subjectiveExam.examFormTests.find(
    (test) => test.testKey === TestKey.SuicidalThoughts
  );

  const selectedAnswer = (
    suicidalQuestion as ExamFormTestChoice
  ).examFormChoiceRows?.find((row) => row.isSelected);

  if (
    selectedAnswer !== undefined &&
    selectedAnswer.id !== String(TestRowKey.NotAtAll)
  ) {
    return 1;
  }

  return 0;
}

export function getDepressionPathwayScoreFromSubjectiveExam(
  subjectiveExam: SubjectiveExamForm
) {
  let score = 0;

  const pointsPerAnswer = {
    [TestRowKey.NotAtAll]: 0,
    [TestRowKey.SeveralDays]: 1,
    [TestRowKey.MoreThanHalfTheDays]: 2,
    [TestRowKey.NearlyEveryDay]: 3,
  };

  for (const examTest of subjectiveExam.examFormTests) {
    const selectedAnswer = (
      examTest as ExamFormTestChoice
    ).examFormChoiceRows?.find((row) => row.isSelected);

    if (!selectedAnswer) {
      continue;
    }

    const points =
      pointsPerAnswer[selectedAnswer.id as keyof typeof pointsPerAnswer];

    score += points;
  }

  return score;
}

export function getFallRiskScoreFromSubjectiveExam(
  subjectiveExam: SubjectiveExamForm
) {
  let score = 0;

  const hadFallenQuestion = subjectiveExam.examFormTests.find(
    (test) => test.testKey === TestKey.HadFallenByLosingBalance
  ) as ExamFormTestChoice;

  const hadFalls = hadFallenQuestion?.examFormChoiceRows?.find(
    (row) => row.id === TestRowKey.RedYes && row.isSelected
  );

  if (hadFalls) {
    const numberOfFallsQuestion = subjectiveExam.examFormTests.find(
      (test) => test.testKey === TestKey.NumberOfFalls
    ) as ExamFormTestData;

    const numberOfFallsRow = numberOfFallsQuestion?.examFormDataInputRows?.find(
      (row) => (row.id as TestRowKey) === TestRowKey.NumberOfFallsRow
    );

    const fallCount = parseInt(numberOfFallsRow?.text ?? "0", 10);

    if (fallCount >= 2) {
      score = 2;
    }
  }

  return { score };
}

export function getFractureFlagScoreFromSubjectiveExam(
  subjectiveExam: SubjectiveExamForm
) {
  //inability to bear weight
  const accidentOrInjuryQuestion = subjectiveExam.examFormTests.find(
    (test) => test.testKey === TestKey.AccidentOrInjury
  ) as ExamFormTestChoice;

  const accidentOrInjuryQuestionSelectedAnswer =
    accidentOrInjuryQuestion.examFormChoiceRows?.find(
      (answer) => answer.isSelected
    );

  if (
    accidentOrInjuryQuestionSelectedAnswer?.id ===
    TestRowKey.AccidentOrInjuryNoBearWeight
  ) {
    return 1;
  }

  return 0;
}

// Onset windows that represent an injury 12+ weeks old, per the same
// WEEK_THRESHOLD the legacy exact-date fallback below uses.
const CHRONIC_ONSET_WINDOWS: InjuryOnsetWindow[] = ["12plusWeeks", "overAYear"];

/**
 * `onsetWindow` (EpisodeOfCare.incident.onsetWindow) is the source of truth
 * going forward. The SOAP "When did this happen?" answer is free text now
 * (e.g. "2-12 weeks") and can't be parsed as a date, so it's only used as a
 * fallback for episodes created before the onset-window dropdown existed.
 */
export function getChronicPainScoreFromSubjectiveExam(
  subjectiveExam: SubjectiveExamForm,
  onsetWindow?: string | null
) {
  if (onsetWindow) {
    return CHRONIC_ONSET_WINDOWS.includes(onsetWindow as InjuryOnsetWindow)
      ? 1
      : 0;
  }

  const chronicPainQuestion = subjectiveExam.examFormTests.find(
    (test) => test.testKey === TestKey.WhenDidOccur
  ) as ExamFormTestData;

  const chronicPainQuestionSelectedAnswer =
    chronicPainQuestion.examFormDataInputRows?.find(
      (answer) => (answer.id as TestRowKey) === TestRowKey.HpcWhenDidOccur
    );

  const date = dayjs(chronicPainQuestionSelectedAnswer?.inputValue);

  const WEEK_THRESHOLD = 12;

  if (date.isBefore(dayjs().subtract(WEEK_THRESHOLD, "week"))) {
    return 1;
  }

  return 0;
}

export function getBMI(
  weight: number,
  height: { feet: number; inches: number }
): number {
  const heightInInches = height.feet * 12 + height.inches;
  const heightInMeters = heightInInches * 0.0254;
  const weightInKilograms = weight * 0.453592;

  return (
    Math.round((weightInKilograms / (heightInMeters * heightInMeters)) * 10) /
    10
  );
}

enum BMICategory {
  Underweight = "underweight",
  Normal = "normal",
  Overweight = "overweight",
  Obese = "obese",
  SevereObese = "severe obese",
}

export function getBMIClassification(bmi: number): BMICategory {
  if (bmi < 18.5) {
    return BMICategory.Underweight;
  }

  if (bmi >= 18.5 && bmi <= 24.9) {
    return BMICategory.Normal;
  }

  if (bmi >= 25 && bmi <= 29.9) {
    return BMICategory.Overweight;
  }

  if (bmi >= 30 && bmi <= 34.9) {
    return BMICategory.Obese;
  }

  if (bmi >= 35) {
    return BMICategory.SevereObese;
  }

  return BMICategory.Normal;
}

interface PathwayScore {
  type: "suicidal" | "depression" | "fallRisk" | "obesity";
  label: string;
  score: number;
  isFlagged: boolean;
  priority: "warning" | "emergency";
}

export function getAllPathwayScores(
  referralHistory: ReferralHistory,
  soap: Soap
): PathwayScore[] {
  const bmi = getBMI(
    referralHistory.patientDetails?.weight as number,
    referralHistory.patientDetails?.height as Height
  );

  const PHQ9_FORM = soap.subjectiveExamForms.exams.find(
    (form) => form.examFormId === "ADBD-ATBD-PHQ9"
  );

  const FRAT_FORM = soap.subjectiveExamForms.exams.find(
    (form) => form.examFormId === "ADBD-ATBD-FRAT"
  );

  if (!PHQ9_FORM || !FRAT_FORM) {
    return [];
  }

  const phqSuicidalScore = getSuicidalPHQ9ScoreFromSubjectiveExam(PHQ9_FORM);
  const depressionPathwayScore =
    getDepressionPathwayScoreFromSubjectiveExam(PHQ9_FORM);

  const { score: fratFallRiskScore } =
    getFallRiskScoreFromSubjectiveExam(FRAT_FORM);

  const THRESHOLD_DEPRESSION_PATHWAY = 10;
  const FALL_RISK_THRESHOLD = 1;

  return [
    {
      isFlagged: phqSuicidalScore > 0,
      priority: "emergency",
      score: phqSuicidalScore,
      type: "suicidal",
      label: "Suicidal Thoughts",
    },
    {
      isFlagged: depressionPathwayScore >= THRESHOLD_DEPRESSION_PATHWAY,
      priority: "warning",
      score: depressionPathwayScore,
      type: "depression",
      label: "Depression Pathway",
    },
    {
      isFlagged: fratFallRiskScore > FALL_RISK_THRESHOLD,
      priority: "warning",
      score: fratFallRiskScore,
      type: "fallRisk",
      label: `Fall Risk`,
    },
    {
      isFlagged: bmi >= 30,
      priority: "warning",
      score: bmi,
      type: "obesity",
      label: `${title(getBMIClassification(bmi))} (${bmi}) BMI`,
    },
  ];
}

export function getSuicidalScore(healthHistory: HealthHistory): PathwayScore {
  const score =
    (healthHistory.depressionRecord.suicidalThoughts ?? 0) > 0 ? 1 : 0;

  return {
    type: "suicidal",
    label: "Suicidal Thoughts",
    score,
    isFlagged: score > 0,
    priority: "emergency",
  };
}

export function getDepressionScore(healthHistory: HealthHistory): PathwayScore {
  const dr = healthHistory.depressionRecord;
  const score =
    dr.littleInterestInDoingThings +
    dr.feelingDownDepressedOrHopeless +
    (dr.sleepIssues ?? 0) +
    (dr.feelingTiredOrLittleEnergy ?? 0) +
    (dr.poorAppetiteOrOverEating ?? 0) +
    (dr.feelingBadAboutSelf ?? 0) +
    (dr.troubleConcentrating ?? 0) +
    (dr.psychomotorChanges ?? 0) +
    (dr.suicidalThoughts ?? 0);

  const DEPRESSION_SCORE_THRESHOLD = 10;

  return {
    type: "depression",
    label: "Depression Pathway",
    score,
    isFlagged: score >= DEPRESSION_SCORE_THRESHOLD,
    priority: "warning",
  };
}
